import { useParams, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { FloxSpinner } from '@tailwindComponents/FloxSpinner';
import { HiOutlineDocumentText, HiArrowLeft } from 'react-icons/hi';
import { BillingCycle } from './invoiceTypes';
import { useAuthHttp } from '../../hooks';
import { INVOICE_LIST } from '../../constants';
import { useAuth } from '../../context';
import { UserRoleService } from '../../services';
import { LSPInvoice } from './LSPInvoice';
import { ShipperInvoice } from './ShipperInvoice';
import { PageHeader } from '../../tailwindComponents/PageHeader';

export function InvoiceDetails() {
  const { user } = useAuth();
  const { isWarehouseClient } = UserRoleService();
  const authHttp = useAuthHttp();
  const navigate = useNavigate();
  const { id } = useParams();
  const [billingCycle, setBillingCycle] = useState<BillingCycle>();
  const [isLoading, setIsLoading] = useState(false);

  const fetchInvoice = async (isStillOnThisInvoice: () => boolean) => {
    setIsLoading(true);
    try {
      const { data } = await authHttp.post(
        INVOICE_LIST,
        {
          ids: [id],
          statuses: isWarehouseClient(user) ? ['Finalised', 'Paid'] : '',
        },
        { headers: { 'Content-Type': 'application/json' } }
      );

      if (data && isStillOnThisInvoice()) {
        setBillingCycle(data.billingCycles[0]);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    } finally {
      if (isStillOnThisInvoice()) setIsLoading(false);
    }
  };

  useEffect(() => {
    let isStillOnThisInvoice = true;
    setBillingCycle(undefined);
    fetchInvoice(() => isStillOnThisInvoice);
    return () => {
      isStillOnThisInvoice = false;
    };
  }, [id]);

  const pages = [
    { name: 'Home', href: '/', current: false },
    { name: 'Invoices', href: '/invoices', current: false },
    { name: 'Invoice Details', href: `/invoice/${id}`, current: true },
  ];

  if (isLoading) {
    return (
      <>
        <PageHeader header='Invoice Details' pages={pages} />
        <main className='flex flex-col gap-6 p-4 lg:px-8 lg:pb-8'>
          <div className='bg-white rounded-lg shadow-sm border border-gray-200 p-12'>
            <div className='flex flex-col items-center gap-3'>
              <FloxSpinner size='md' />
              <p className='text-gray-500 text-sm'>Loading invoice...</p>
            </div>
          </div>
        </main>
      </>
    );
  }

  if (!billingCycle) {
    return (
      <>
        <PageHeader header='Invoice Details' pages={pages} />
        <main className='flex flex-col gap-6 p-4 lg:px-8 lg:pb-8'>
          <div className='bg-white rounded-lg shadow-sm border border-gray-200 p-12'>
            <div className='flex flex-col items-center gap-4'>
              <div className='w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center'>
                <HiOutlineDocumentText className='w-8 h-8 text-gray-400' />
              </div>
              <div className='text-center'>
                <h3 className='text-lg font-semibold text-gray-900'>
                  Invoice not found
                </h3>
                <p className='text-gray-500 text-sm mt-1'>
                  The invoice you&apos;re looking for doesn&apos;t exist or you
                  don&apos;t have permission to view it.
                </p>
              </div>
              <button
                type='button'
                onClick={() => navigate('/invoices')}
                className='mt-2 inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors'
              >
                <HiArrowLeft className='w-4 h-4' />
                Back to Invoices
              </button>
            </div>
          </div>
        </main>
      </>
    );
  }

  return isWarehouseClient(user) ? (
    <ShipperInvoice
      key={billingCycle.id}
      billingCycle={billingCycle}
      invoice={billingCycle.floxToShipperInvoice}
    />
  ) : (
    <LSPInvoice
      key={billingCycle.id}
      billingCycle={billingCycle}
      invoice={billingCycle.lspToFloxInvoice}
    />
  );
}
